import { CheckCircle2, Circle, Loader2, X, ChevronRight, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding, useDismissOnboarding, pickNextActionableStep } from '@/hooks/useOnboarding';
import { Button } from '@/components/ui/button';
import type { StepStatus } from '@/api/onboarding';

function StepIcon({ status }: { status: StepStatus['status'] }) {
  if (status === 'completed') {
    return <CheckCircle2 className="h-5 w-5 text-status-allow shrink-0" />;
  }
  if (status === 'in_progress') {
    return <Loader2 className="h-5 w-5 text-primary shrink-0 animate-spin" />;
  }
  return <Circle className="h-5 w-5 text-muted-foreground shrink-0" />;
}

export function SetupChecklist() {
  const { data, isLoading } = useOnboarding();
  const dismiss = useDismissOnboarding();
  const navigate = useNavigate();

  if (isLoading || !data) return null;

  const { data: onboarding, dismissed } = data;

  if (dismissed || onboarding.completed_count >= onboarding.total_count) return null;

  const completedPct = onboarding.total_count > 0
    ? Math.round((onboarding.completed_count / onboarding.total_count) * 100)
    : 0;

  const sortedSteps = [...onboarding.steps].sort((a, b) => a.order - b.order);
  const nextStep = pickNextActionableStep(onboarding.steps);

  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <h2 className="text-base font-semibold">Setup Checklist</h2>
          <p className="text-sm text-muted-foreground">
            {onboarding.completed_count} of {onboarding.total_count} steps completed
          </p>
        </div>
        <button
          onClick={() => dismiss.mutate()}
          disabled={dismiss.isPending}
          aria-label="Dismiss setup checklist"
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all duration-300"
          style={{ width: `${completedPct}%` }}
        />
      </div>

      {/* Steps */}
      <div className="space-y-1">
        {sortedSteps.map((step) => (
          <div
            key={step.id}
            className="flex items-start gap-3 rounded-md px-3 py-3 hover:bg-muted/50 transition-colors"
          >
            <StepIcon status={step.status} />

            <div className="flex-1 min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{step.label}</span>
                {step.evidence_count > 0 && (
                  <span className="text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 font-medium">
                    {step.evidence_count} connected
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{step.description}</p>
              {step.blocking_reason && (
                <p className="text-xs text-destructive">{step.blocking_reason}</p>
              )}
            </div>

            {step.status !== 'completed' && (
              step.id === nextStep?.id ? (
                <Button
                  size="sm"
                  onClick={() => navigate(step.cta_target)}
                  className="shrink-0 gap-1.5"
                >
                  {step.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <button
                  onClick={() => navigate(step.cta_target)}
                  className="flex items-center gap-1 text-xs text-primary font-medium hover:underline shrink-0"
                >
                  Go
                  <ChevronRight className="h-3 w-3" />
                </button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
